import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { main_url } from './pages/basefile';
import { getKeycloak } from './auth/keycloak';
import ScheduleCalendar from './pages/ScheduleCalender';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CssBaseline,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { DataGrid } from '@mui/x-data-grid';
import { fetchPatientDatagridRows } from './services/patientService';
import { fetchProjectDatagridRows } from './services/projectService';
import { formatDate } from './utils/dateFormat';
import DatagridNavLink from './components/DatagridNavLink';

async function redirectToKeycloakLogin() {
  localStorage.removeItem('authToken');
  const kc = getKeycloak();
  try {
    await kc.login({ redirectUri: `${window.location.origin}/` });
  } catch (e) {
    console.error('Keycloak login redirect failed', e);
  }
}

const summaryCardContentSx = {
  p: { xs: 1, sm: 1.5, md: 2 },
  '&:last-child': { pb: { xs: 1, sm: 1.5, md: 2 } },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: { xs: 0.25, md: 0.5 },
};

function DashboardStatCard({
  title,
  mobileTitle,
  value,
  icon: Icon,
  iconColor,
  gradient,
  onView,
  viewDisabled,
  compactMobile = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const label = isMobile && mobileTitle ? mobileTitle : title;

  return (
    <Card
      sx={{
        height: '100%',
        width: { xs: '100%', md: 220 },
        maxWidth: { xs: '100%', md: 220 },
        minWidth: 0,
        borderRadius: { xs: 1.5, md: 2 },
        background: gradient,
        boxShadow: { xs: 1, md: 2 },
      }}
    >
      <CardContent sx={summaryCardContentSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: { xs: 0.5, md: 1 }, minWidth: 0 }}>
          <Box sx={{ minWidth: 0, flex: 1, pr: { md: 1 } }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                display: { xs: '-webkit-box', md: 'block' },
                WebkitLineClamp: compactMobile ? 3 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: { xs: 'hidden', md: 'visible' },
                lineHeight: { xs: 1.15, md: 1.25 },
                fontSize: { xs: '0.58rem', sm: '0.72rem', md: '0.875rem' },
                minHeight: { md: 38 },
                whiteSpace: { md: 'normal' },
                overflowWrap: 'anywhere',
                wordBreak: { xs: 'break-word', md: 'normal' },
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                fontFamily: 'monospace',
                fontSize: { xs: '1rem', sm: '1.35rem', md: '2.125rem' },
                mt: { xs: 0.25, md: 1 },
                lineHeight: { xs: 1.1, md: 1.2 },
              }}
            >
              {value}
            </Typography>
          </Box>
          <Icon
            sx={{
              fontSize: { xs: compactMobile ? 0 : 22, sm: 30, md: 40 },
              color: iconColor,
              flexShrink: 0,
              display: { xs: compactMobile ? 'none' : 'block', md: 'block' },
            }}
          />
        </Box>
        <Button
          size="small"
          sx={{
            p: 0,
            minHeight: { xs: 22, md: 'auto' },
            mt: { md: 1 },
            fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.875rem' },
            alignSelf: 'flex-start',
          }}
          disabled={viewDisabled}
          onClick={onView}
        >
          View list
        </Button>
      </CardContent>
    </Card>
  );
}

const Dashboard1 = () => {
  const token = localStorage.getItem('authToken');

  const [summary, setSummary] = useState({
    active_projects_count: 0,
    patients_count: 0,
    completed_forms_last_30_days: [],
    upcoming_appointments_30_days: [],
    recent_questionnaires_14_days: [],
    upcoming_projects: [],
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [projectsOpen, setProjectsOpen] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [patientsOpen, setPatientsOpen] = useState(false);
  const [patientsList, setPatientsList] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const [completedOpen, setCompletedOpen] = useState(false);
  const [upcomingOpen, setUpcomingOpen] = useState(false);

  const recentQuestionnaireColumns = useMemo(
    () => [
      {
        field: 'patient_name',
        headerName: 'Subject',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.patient_id ? `/subject-management/edit/${p.row.patient_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      { field: 'last_completed_at', headerName: 'Last Completed At', flex: 1, minWidth: 200 },
      { field: 'count', headerName: 'Completed Forms', width: 160, type: 'number' },
    ],
    []
  );

  const upcomingProjectColumns = useMemo(
    () => [
      {
        field: 'pid',
        headerName: 'Project ID',
        width: 110,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/project-details/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
      {
        field: 'name',
        headerName: 'Project Name',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/project-details/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
      {
        field: 'client_name',
        headerName: 'Client',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.client_id ? `/client-management/edit/${p.row.client_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      { field: 'status', headerName: 'Status', width: 120 },
    ],
    []
  );

  const projectDialogColumns = useMemo(
    () => [
      {
        field: 'project_id',
        headerName: 'Project ID',
        flex: 1.1,
        minWidth: 140,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/project-details/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
      {
        field: 'client_name',
        headerName: 'Client Name',
        flex: 1.3,
        minWidth: 170,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.client_id ? `/client-management/edit/${p.row.client_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      {
        field: 'project_name',
        headerName: 'Project Name',
        flex: 1.4,
        minWidth: 180,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/project-details/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
      {
        field: 'project_start_date',
        headerName: 'Project Start Date',
        flex: 1,
        minWidth: 150,
        renderCell: (p) => formatDate(p.value),
      },
      {
        field: 'project_end_date',
        headerName: 'Project End Date',
        flex: 1,
        minWidth: 150,
        renderCell: (p) => formatDate(p.value),
      },
      {
        field: 'number_of_schedule',
        headerName: 'Number of Schedule',
        type: 'number',
        flex: 1,
        minWidth: 150,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/project-details/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
      {
        field: 'number_of_patient',
        headerName: 'Number of Subjects',
        type: 'number',
        flex: 1,
        minWidth: 140,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/project-details/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
    ],
    []
  );

  const patientDialogColumns = useMemo(
    () => [
      {
        field: 'pid',
        headerName: 'PID',
        minWidth: 120,
        flex: 0.9,
        renderCell: (p) => (
          <DatagridNavLink to={p.row.id ? `/subject-management/edit/${p.row.id}` : ''}>{p.value}</DatagridNavLink>
        ),
      },
      { field: 'first_name', headerName: 'First Name', minWidth: 140, flex: 1 },
      { field: 'last_name', headerName: 'Last Name', minWidth: 140, flex: 1 },
      { field: 'email', headerName: 'Email', minWidth: 180, flex: 1.2 },
      { field: 'phone', headerName: 'Phone', minWidth: 130, flex: 0.9 },
      {
        field: 'client_name',
        headerName: 'Client',
        minWidth: 140,
        flex: 1,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.client_id ? `/client-management/edit/${p.row.client_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      {
        field: 'project_name',
        headerName: 'Project',
        minWidth: 140,
        flex: 1,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.project_id ? `/project-details/${p.row.project_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
    ],
    []
  );

  const completedFormsColumns = useMemo(
    () => [
      {
        field: 'patient_name',
        headerName: 'Subject',
        flex: 1,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.patient_id ? `/subject-management/edit/${p.row.patient_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      { field: 'count', headerName: 'Completed Forms', width: 180, type: 'number' },
    ],
    []
  );

  const upcomingAppointmentsColumns = useMemo(
    () => [
      {
        field: 'patient_name',
        headerName: 'Subject',
        flex: 1,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.patient_id ? `/subject-management/edit/${p.row.patient_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      {
        field: 'pid',
        headerName: 'Project ID',
        width: 160,
        renderCell: (p) => (
          <DatagridNavLink
            to={p.row.project_db_id ? `/project-details/${p.row.project_db_id}` : ''}
          >
            {p.value}
          </DatagridNavLink>
        ),
      },
      { field: 'actual_date', headerName: 'Appointment Date', width: 170 },
    ],
    []
  );

  const fetchDashboardSummary = async () => {
    if (!token) {
      await redirectToKeycloakLogin();
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await fetch(`${main_url}/api/v1/dashboard/summary`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        await redirectToKeycloakLogin();
        return;
      }
      if (res.status === 403) {
        setSummaryError('You do not have access to this dashboard.');
        return;
      }
      if (!res.ok) throw new Error('Failed to load dashboard summary');
      const data = await res.json();
      setSummary({
        active_projects_count: data.active_projects_count ?? 0,
        patients_count: data.patients_count ?? 0,
        completed_forms_last_30_days: data.completed_forms_last_30_days ?? [],
        upcoming_appointments_30_days: data.upcoming_appointments_30_days ?? [],
        recent_questionnaires_14_days: data.recent_questionnaires_14_days ?? [],
        upcoming_projects: data.upcoming_projects ?? [],
      });
    } catch (err) {
      setSummaryError(err.message || 'Error loading dashboard');
    } finally {
      setSummaryLoading(false);
    }
  };

  const openPatientsPopup = async () => {
    setPatientsOpen(true);
    setPatientsLoading(true);
    try {
      const data = await fetchPatientDatagridRows({ page: 0, pageSize: 50, patientStatus: "active" });
      const rows = data?.rows || data?.items || [];
      setPatientsList(rows);
    } catch (err) {
      console.error('Failed to load patients for popup', err);
      setPatientsList([]);
    } finally {
      setPatientsLoading(false);
    }
  };

  const openProjectsPopup = async () => {
    setProjectsOpen(true);
    setProjectsLoading(true);
    try {
      const data = await fetchProjectDatagridRows({
        page: 0,
        pageSize: 50,
        status: 'active',
      });
      const rows = data?.rows || data?.items || [];
      setProjectsList(rows);
    } catch (err) {
      console.error('Failed to load active projects for popup', err);
      setProjectsList([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardSummary();
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#dde7ecff',
        py: 3,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1600,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        {/* Left: dashboard container (dark blue) */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            boxShadow: 4,
            bgcolor: '#1a245aff',
          }}
        >
          <CssBaseline />
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontSize: { xs: '1.35rem', md: '2rem' }, fontWeight: 'bold', mt: 0, ml: 0, color: '#ffffff' }}
          >
            Dashboard
          </Typography>
          {summaryError && (
            <Typography color="error" sx={{ mb: 1 }}>{summaryError}</Typography>
          )}

          <Grid container spacing={{ xs: 1, sm: 1.5, md: 3 }} sx={{ width: '100%', mt: { xs: 0.5, md: 1 } }}>
            <Grid item xs={6} md={3} sx={{ minWidth: 0, display: { xs: 'flex', md: 'block' } }}>
              <DashboardStatCard
                title="Active Projects"
                mobileTitle="Active projects"
                value={summaryLoading ? '…' : summary.active_projects_count}
                icon={WorkOutlineIcon}
                iconColor="#1a237e"
                gradient="linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                onView={openProjectsPopup}
              />
            </Grid>
            <Grid item xs={6} md={3} sx={{ minWidth: 0, display: { xs: 'flex', md: 'block' } }}>
              <DashboardStatCard
                title="Active subjects"
                value={summaryLoading ? '…' : summary.patients_count}
                icon={PeopleAltIcon}
                iconColor="#1b5e20"
                gradient="linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
                onView={openPatientsPopup}
              />
            </Grid>
            <Grid item xs={6} md={3} sx={{ minWidth: 0, display: { xs: 'flex', md: 'block' } }}>
              <DashboardStatCard
                title="Last 30 days – Completed schedule forms"
                mobileTitle="Completed forms (30d)"
                compactMobile
                value={
                  summaryLoading
                    ? '…'
                    : (summary.completed_forms_last_30_days || []).reduce(
                        (sum, row) => sum + (row.count || 0),
                        0,
                      )
                }
                icon={AssignmentTurnedInIcon}
                iconColor="#e65100"
                gradient="linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
                onView={() => setCompletedOpen(true)}
                viewDisabled={
                  summaryLoading ||
                  !(summary.completed_forms_last_30_days || []).some((row) => row.count > 0)
                }
              />
            </Grid>
            <Grid item xs={6} md={3} sx={{ minWidth: 0, display: { xs: 'flex', md: 'block' } }}>
              <DashboardStatCard
                title="Upcoming appointments (30 days)"
                mobileTitle="Appointments (30d)"
                compactMobile
                value={summaryLoading ? '…' : (summary.upcoming_appointments_30_days || []).length}
                icon={EventAvailableIcon}
                iconColor="#880e4f"
                gradient="linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)"
                onView={() => setUpcomingOpen(true)}
                viewDisabled={summaryLoading || !(summary.upcoming_appointments_30_days || []).length}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 1.5, sm: 2 }, width: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ffffff', mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Upcoming Schedule
            </Typography>
            <Box sx={{ width: '100%', borderRadius: 2, bgcolor: '#ffffff', p: { xs: 1.5, sm: 2 }, pb: 3, boxShadow: 2 }}>
              <ScheduleCalendar execution_pk={10} />
            </Box>
          </Box>
        </Box>

        {/* Right: vertical widgets outside dashboard container */}
        <Box
          sx={{
            flex: '0 0 auto',
            width: { xs: '100%', md: 380 },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Card sx={{ borderRadius: 2, bgcolor: '#ffffff', boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Recently Completed Questionnaires
                </Typography>
                <AssignmentTurnedInIcon sx={{ color: '#e65100' }} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Subjects who recently completed questionnaires (last 14 days)
              </Typography>
              <Box sx={{ height: 320, mt: 1 }}>
                <DataGrid
                  rows={(summary.recent_questionnaires_14_days || []).map((row, index) => ({
                    id: row.patient_id ?? index,
                    patient_id: row.patient_id,
                    patient_name: row.patient_name || `Subject ${row.patient_id ?? index}`,
                    last_completed_at: formatDate(row.last_completed_at, ''),
                    count: row.count ?? 0,
                  }))}
                  columns={recentQuestionnaireColumns}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  pageSizeOptions={[5, 10, 20]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
                  }
                  sx={{
                    '& .row-even': { backgroundColor: '#ffffff' },
                    '& .row-odd': { backgroundColor: '#f5f5f5' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, bgcolor: '#ffffff', boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Upcoming Projects
                </Typography>
                <WorkOutlineIcon sx={{ color: '#1a237e' }} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Projects that are pending or submitted (not yet active)
              </Typography>
              <Box sx={{ height: 320, mt: 1 }}>
                <DataGrid
                  rows={(summary.upcoming_projects || []).map((row, index) => ({
                    id: row.id ?? index,
                    pid: row.pid || '',
                    name: row.name || '',
                    client_id: row.client_id || '',
                    client_name: row.client_name || '',
                    status: row.status || '',
                  }))}
                  columns={upcomingProjectColumns}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  pageSizeOptions={[5, 10, 20]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
                  }
                  sx={{
                    '& .row-even': { backgroundColor: '#ffffff' },
                    '& .row-odd': { backgroundColor: '#f5f5f5' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Dialog open={projectsOpen} onClose={() => setProjectsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Active Projects</DialogTitle>
        <DialogContent>
          {projectsLoading ? (
            <Typography>Loading…</Typography>
          ) : (
            <Box sx={{ height: 400, mt: 1 }}>
              <DataGrid
                rows={(projectsList || []).map((row, index) => ({
                  // Preserve original datagrid shape from /project, just ensure an id is present
                  id: row.id ?? row.project_id ?? row.pid ?? index,
                  ...row,
                }))}
                columns={projectDialogColumns}
                hideFooterSelectedRowCount
                disableRowSelectionOnClick
                pageSizeOptions={[5, 10, 20]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
                }
                sx={{
                  '& .row-even': {
                    backgroundColor: '#ffffff',
                  },
                  '& .row-odd': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={patientsOpen} onClose={() => setPatientsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Active subjects</DialogTitle>
        <DialogContent>
          {patientsLoading ? (
            <Typography>Loading…</Typography>
          ) : (
            <Box sx={{ height: 400, mt: 1 }}>
              <DataGrid
                rows={(patientsList || []).map((row, index) => ({
                  // Mirror /subject-management datagrid row shape, ensuring id is present
                  id: row.id ?? row.patient_id ?? index,
                  ...row,
                }))}
                columns={patientDialogColumns}
                hideFooterSelectedRowCount
                disableRowSelectionOnClick
                pageSizeOptions={[5, 10, 20]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
                }
                sx={{
                  '& .row-even': {
                    backgroundColor: '#ffffff',
                  },
                  '& .row-odd': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatientsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={completedOpen}
        onClose={() => setCompletedOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Completed schedule forms – last 30 days</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, mt: 1 }}>
            <DataGrid
              rows={(summary.completed_forms_last_30_days || []).map((row, index) => ({
                id: row.patient_id ?? index,
                patient_id: row.patient_id,
                patient_name: row.patient_name || `Subject ${row.patient_id ?? index}`,
                count: row.count ?? 0,
              }))}
              columns={completedFormsColumns}
              hideFooterSelectedRowCount
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 20]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
              }
              sx={{
                '& .row-even': {
                  backgroundColor: '#ffffff',
                },
                '& .row-odd': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletedOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={upcomingOpen}
        onClose={() => setUpcomingOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upcoming appointments – next 30 days</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, mt: 1 }}>
            <DataGrid
              rows={(summary.upcoming_appointments_30_days || []).map((row, index) => ({
                id: `${row.patient_id ?? 'p'}-${row.project_id ?? 'proj'}-${index}`,
                patient_id: row.patient_id,
                patient_name: row.patient_name || `Subject ${row.patient_id ?? index}`,
                project_db_id: row.project_id,
                pid: row.pid || '',
                actual_date: formatDate(row.actual_date, ''),
              }))}
              columns={upcomingAppointmentsColumns}
              hideFooterSelectedRowCount
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 20]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
              }
              sx={{
                '& .row-even': {
                  backgroundColor: '#ffffff',
                },
                '& .row-odd': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpcomingOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard1;
